import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IFilter, IFiltersForm, IKPIChartState } from './Charts.interface';
import { getFilters } from 'src/services/KPI/KPIForm/KPIForm.service';
import { getChartKPI } from 'src/services/KPI/Charts/Charts.service';
import { IChartKPI } from 'src/services/KPI/Charts/Charts.interface';

/**
 * The initial state for the KPI Chart slice.
 *
 * @constant
 * @type {IKPIChartState}
 */
const initialState: IKPIChartState = {
  charts: [],
  filter: {
    areaName: '',
    monthNumber: 0,
    periodId: 0,
    quarterId: 0,
    channelId: 0,
  },
  filters: [],
  error: null,
  loading: false,
};

/**
 * A slice of the Redux store responsible for managing KPI chart data and filters.
 *
 * This slice contains actions and reducers to control the KPI chart's filters, 
 * manage loading states, and handle API responses for retrieving filters and chart data.
 *
 * @constant
 * @type {Slice<IKPIChartState>}
 */
const KPIFormSlice = createSlice({
  name: 'KPIForm',
  initialState,
  reducers: {
    /**
     * Sets the filter based on the provided payload.
     *
     * @param {IKPIChartState} state - The current state of the KPIForm slice.
     * @param {PayloadAction<IFilter>} action - The action containing the new filter values.
     */
    setFilter: (state, action: PayloadAction<IFilter>) => {
      state.filter = { ...state.filter, ...action.payload };
    },

    /**
     * Resets the filters to the initial state.
     *
     * @param {IKPIChartState} state - The current state of the KPIForm slice.
     */
    resetFilters: (state) => {
      state.filter = initialState.filter;
    },

    /**
     * Resets the charts to an empty array.
     *
     * @param {IKPIChartState} state - The current state of the KPIForm slice.
     */
    resetCharts: (state) => {
      state.charts = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getFilters.fulfilled,
        (state, action: PayloadAction<IFiltersForm[]>) => {
          state.loading = false;
          state.filters = action.payload;
        },
      )
      .addCase(getFilters.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(
        getChartKPI.fulfilled,
        (state, action: PayloadAction<IChartKPI[]>) => {
          state.loading = false;
          state.charts = action.payload;
        },
      )
      .addCase(getChartKPI.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

/**
 * Action creators generated from the KPIForm slice.
 *
 * @constant
 * @type {{ setFilter: (filter: IFilter) => PayloadAction<IFilter>, resetFilters: () => PayloadAction<void>, resetCharts: () => PayloadAction<void> }}
 */
export const { setFilter, resetFilters, resetCharts } = KPIFormSlice.actions;

/**
 * The reducer for the KPIForm slice, to be used in the Redux store.
 *
 * @constant
 * @type {Reducer<IKPIChartState>}
 */
export default KPIFormSlice.reducer;
